import { CommonModule } from '@angular/common';
import { afterNextRender, ChangeDetectorRef, Component } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { finalize } from 'rxjs';
import { SftpApi, SftpProcessedFile, SpringPage } from '../../core/sftp-api';
import { dateTime, statusClass, text } from '../../shared/formatters';
import { PageNavigation } from '../../shared/page-navigation/page-navigation';

@Component({ selector: 'app-gp-sftp', imports: [CommonModule, FormsModule, PageNavigation], templateUrl: './gp-sftp.html' })
export class GpSftp {
  readonly statuses = ['PROCESSING', 'PROCESSED', 'FAILED'];
  readonly pageSizes = [10, 25, 50, 100];
  readonly text = text;
  readonly dateTime = dateTime;
  readonly statusClass = statusClass;
  rows: SftpProcessedFile[] = [];
  filters: Record<string, string | number> = {};
  page = 0;
  size = 10;
  total = 0;
  totalPages = 1;
  loading = false;
  uploading = false;
  filtersOpen = false;
  error = '';
  message = '';
  selectedFiles: File[] = [];

  constructor(private readonly api: SftpApi, private readonly cdr: ChangeDetectorRef) {
    afterNextRender(() => this.load());
  }

  chooseFile(event: Event): void {
    this.selectedFiles = Array.from((event.target as HTMLInputElement).files ?? []);
    this.message = '';
  }

  upload(): void {
    if (!this.selectedFiles.length || this.uploading) return;
    this.uploading = true; this.error = ''; this.message = '';
    const uploadedNames = this.selectedFiles.map(file => file.name).join(', ');
    this.api.upload(this.selectedFiles).pipe(finalize(() => { this.uploading = false; this.cdr.markForCheck(); })).subscribe({
      next: () => { this.message = `Archivos procesados y evento enviado: ${uploadedNames}.`; this.selectedFiles = []; this.page = 0; this.load(); },
      error: error => {
        this.error = this.parseApiError(error, 'No se pudo procesar el archivo.');
        window.alert(this.error);
        this.cdr.markForCheck();
      }
    });
  }

  get selectedFileNames(): string { return this.selectedFiles.map(file => file.name).join(', '); }

  load(): void {
    this.loading = true; this.error = '';
    this.api.findAll(this.page, this.size, this.buildRsql()).pipe(finalize(() => { this.loading = false; this.cdr.markForCheck(); })).subscribe({
      next: (result: SpringPage<SftpProcessedFile>) => {
        this.rows = result.content ?? []; this.total = result.totalElements ?? 0; this.totalPages = Math.max(1, result.totalPages ?? 1); this.cdr.markForCheck();
      },
      error: () => { this.error = 'No se pudo cargar el historial de archivos SFTP.'; this.cdr.markForCheck(); }
    });
  }

  applyFilters(): void { this.page = 0; this.load(); }
  clearFilters(): void { this.filters = {}; this.page = 0; this.load(); }
  previous(): void { if (this.page > 0 && !this.loading) { this.page--; this.load(); } }
  next(): void { if (this.page + 1 < this.totalPages && !this.loading) { this.page++; this.load(); } }
  formatBytes(value: number): string {
    if (value < 1024) return `${value} B`;
    if (value < 1024 ** 2) return `${(value / 1024).toFixed(1)} KB`;
    return `${(value / 1024 ** 2).toFixed(1)} MB`;
  }

  private buildRsql(): string {
    const expressions: string[] = [];
    const append = (key: string, template: string) => {
      const value = this.filters[key];
      if (value !== undefined && value !== '') expressions.push(template.replace('%s', this.escape(String(value))));
    };
    append('id', 'id==%s'); append('fileName', 'fileName==*%s*'); append('remotePath', 'remotePath==*%s*');
    append('status', 'status==%s'); append('createdFrom', 'createdAt=ge=%s'); append('createdTo', 'createdAt=le=%s');
    return expressions.join(';');
  }

  private escape(value: string): string { return value.replace(/["'();,=*!<>\\]/g, '\\$&'); }

  private parseApiError(error: unknown, fallback: string): string {
    const body = (error as { error?: unknown })?.error;
    if (body && typeof body === 'object' && 'message' in body) {
      const message = (body as { message?: unknown }).message;
      if (typeof message === 'string' && message.trim()) return message;
    }
    if (typeof body === 'string') {
      try {
        const parsed = JSON.parse(body) as { message?: unknown };
        if (typeof parsed.message === 'string' && parsed.message.trim()) return parsed.message;
      } catch {
        if (body.trim()) return body;
      }
    }
    return fallback;
  }
}
